// Pure helpers for chunk-load recovery: recognise a failed dynamic import and
// allow at most ONE automatic full reload per build per browser session.

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const CHUNK_RELOAD_KEY_PREFIX = "administr:chunk-reload:";

export function chunkReloadKey(buildId: string | null | undefined): string {
  return `${CHUNK_RELOAD_KEY_PREFIX}${buildId || "unknown"}`;
}

/**
 * True exactly once per (storage, buildId): the first call sets the flag and
 * returns true (reload now); later calls return false (let the error surface).
 * No storage (private mode quirks) means no reload, so a broken chunk can never
 * cause a reload loop.
 */
export function shouldReloadForChunkError(
  storage: KeyValueStorage | null | undefined,
  buildId: string | null | undefined,
): boolean {
  if (!storage) return false;
  const key = chunkReloadKey(buildId);
  try {
    if (storage.getItem(key)) return false;
    storage.setItem(key, "1");
    return true;
  } catch {
    return false;
  }
}

// Chrome / Firefox / Safari wordings for a dynamic import() that 404s or
// fails, plus webpack-style ChunkLoadError and Vite's CSS preload failure.
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
  /Unable to preload CSS/i,
];

export function isChunkLoadError(reason: unknown): boolean {
  if (!reason) return false;
  if (typeof reason === "string") return CHUNK_ERROR_PATTERNS.some((p) => p.test(reason));
  if (typeof reason !== "object") return false;
  const { name, message } = reason as { name?: unknown; message?: unknown };
  if (name === "ChunkLoadError") return true;
  return typeof message === "string" && CHUNK_ERROR_PATTERNS.some((p) => p.test(message));
}
